import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import type { ApiKeyConnectionSettings } from "../../../../schemas/ApiKeyConnection/settings/index";
import { OAuthConnectionResponseSchema } from "../../oauth/schemas/list-connections.schema";
import { EmbedAgentSchema } from "./embed-public.schema";

/**
 * Settings schema for API response.
 * Service-specific settings (bot info, webhook status, etc.) as JSON object.
 *
 * NOTE: We use z.record(z.string(), z.unknown()) instead of individual schemas because:
 * 1. Settings structure varies by service type (Telegram, Twilio, Slack, Teams)
 * 2. Service type is in connection.service column, not settings JSON
 * 3. Backend validates with service-specific schemas (TelegramConnectionSettingsSchema, etc.)
 */
const ApiKeyConnectionSettingsResponseSchema = z.union([
  z.custom<ApiKeyConnectionSettings>(),
  z.record(z.string(), z.unknown()) // Fallback for any object structure
]);

/**
 * ApiKeyConnection Response Schema (without sensitive credentials)
 * Represents an API key connection (Telegram, Twilio, etc.)
 */
/** A2P 10DLC registration status (nested on ApiKeyConnection for Twilio connections) */
const A2pRegistrationStatusSchema = z.object({
  status: z.nativeEnum(DbEnum.A2pRegistrationStatus)
});

export const ApiKeyConnectionResponseSchema = z.object({
  id: z.string().uuid(),
  service: z.nativeEnum(DbEnum.ApiKeyService),
  status: z.nativeEnum(DbEnum.OAuthConnectionStatus), // Reuses OAuth status enum
  failureReason: z.string().nullable(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  /** Service-specific settings (bot info, webhook status, commands, etc.) */
  settings: ApiKeyConnectionSettingsResponseSchema.nullable().optional(),
  lastValidatedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  /** Parent connection ID (for child connections like FB_MESSENGER_PAGE -> TWILIO) */
  apiKeyConnectionId: z.string().uuid().nullable().optional(),
  /** A2P 10DLC registration status (US SMS compliance — Twilio connections only) */
  a2pRegistration: A2pRegistrationStatusSchema.nullable().optional()
});

export type ApiKeyConnectionResponse = z.infer<typeof ApiKeyConnectionResponseSchema>;

/**
 * Deployment Response Schema
 *
 * Represents a deployment (Deployment entity) returned from API.
 * This matches the TypedDeployment shape but with Zod validation.
 *
 * Supports:
 * - Legacy: tokens stored in deploymentSettings (both connection IDs = null)
 * - OAuth: tokens in OAuthConnection table (oauthConnectionId populated)
 * - ApiKey: credentials in ApiKeyConnection table (apiKeyConnectionId populated)
 */
export const DeploymentResponseSchema = z.object({
  id: z.string().uuid(),
  /**
   * No `.min(1)`, and that is a decision about the RESPONSE direction rather
   * than an admission about the data. Blankness is refused on the WRITE paths
   * (`CreateDeploymentBodySchema`, `UpdateDeploymentBodySchema`, the v1 twins,
   * `DeploymentEntity.update`); the generated client `.parse()`s this schema on
   * every read, so a minimum here would turn one bad row into a thrown hook and
   * a blank screen, where today it is one empty label on an otherwise working
   * page. A read-side refusal is the more expensive failure, not the safer one.
   */
  name: z.string(),
  description: z.string().nullable(),
  type: z.nativeEnum(DbEnum.DeploymentType),
  deploymentSettings: z.record(z.string(), z.unknown()), // TODO: Use typed schemas from src/schemas/deployment/providers
  createdAt: z.string().datetime(), // API serializes as ISO string
  updatedAt: z.string().datetime().nullable(),
  organizationId: z.string(),
  phoneNumberId: z.string().uuid().nullable(),
  /**
   * Phone number details for phone-anchored deployments (WhatsApp,
   * Twilio SMS, Twilio Voice, FB Messenger). `number` is the full
   * E.164 string ("+14155551234") shown verbatim in the inbox details
   * pane and other UI surfaces; `countryCode` is the ISO country
   * (e.g. "US") used by A2P 10DLC validation. Null for non-phone
   * deployments (Slack, Telegram, Email, Teams, Embed).
   */
  phoneNumber: z.object({ number: z.string(), countryCode: z.string() }).nullable().optional(),
  isActive: z.boolean().optional(),

  // Agent - moved from deploymentSettings.agentId to dedicated column
  /** FK to Agent table (null for draft deployments) */
  agentId: z.string().uuid().nullable().optional(),
  /** Populated agent object (when agentId exists) - for frontend convenience */
  agent: EmbedAgentSchema.nullable().optional(),

  // OAuthConnection support (Gmail, Slack, etc.)
  /** FK to OAuthConnection table (null for non-OAuth deployments) */
  oauthConnectionId: z.string().uuid().nullable().optional(),
  /** Populated OAuthConnection (when oauthConnectionId exists) - NO TOKENS */
  oauthConnection: OAuthConnectionResponseSchema.nullable().optional(),

  // ApiKeyConnection support (Telegram, Twilio, etc.)
  /** FK to ApiKeyConnection table (null for non-ApiKey deployments) */
  apiKeyConnectionId: z.string().uuid().nullable().optional(),
  /** Populated ApiKeyConnection (when apiKeyConnectionId exists) - NO CREDENTIALS */
  apiKeyConnection: ApiKeyConnectionResponseSchema.nullable().optional()
});

export type DeploymentResponse = z.infer<typeof DeploymentResponseSchema>;
